import { BaseLabelJSON } from "../../../constants/exportsCountries"
import { DOMESTIC_NUTRITION_KEYS } from "../../../Utils/constants"

export type CanadaMandatoryNutrient =
  | typeof CANADA_NUTRITION_KEYS.ENERGY
  | typeof CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE
  | typeof CANADA_NUTRITION_KEYS.TOTAL_SUGARS
  | typeof CANADA_NUTRITION_KEYS.DIETARY_FIBRE
  | typeof CANADA_NUTRITION_KEYS.CALCIUM
  | typeof CANADA_NUTRITION_KEYS.IRON
  | typeof CANADA_NUTRITION_KEYS.POTASSIUM
  | typeof CANADA_NUTRITION_KEYS.PROTEIN
  | typeof CANADA_NUTRITION_KEYS.TOTAL_FAT
  | typeof CANADA_NUTRITION_KEYS.SATURATED_FAT
  | typeof CANADA_NUTRITION_KEYS.SODIUM
  | typeof CANADA_NUTRITION_KEYS.CHOLESTEROL
  | typeof CANADA_NUTRITION_KEYS.TRANS_FAT

export type CanadaMandatoryNutrientObject = Record<
  CanadaMandatoryNutrient,
  string
>

export const CANADA_NUTRITION_KEYS = {
  ...DOMESTIC_NUTRITION_KEYS,
  CALCIUM: "Calcium",
  IRON: "Iron",
  POTASSIUM: "Potassium",
}

export interface CanadaLabelJson extends BaseLabelJSON {
  mandatory_nutrient: [CanadaMandatoryNutrientObject]
  translated_household_quantity: string
}

export type CanadaNonMandatoryInterleavedNutrient =
  | typeof CANADA_NUTRITION_KEYS.PUFA
  | typeof CANADA_NUTRITION_KEYS.MUFA
  | typeof CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS
  | typeof CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS

export const INTERLEAVED_NUTRIENTS: CanadaNonMandatoryInterleavedNutrient[] = [
  CANADA_NUTRITION_KEYS.PUFA,
  CANADA_NUTRITION_KEYS.MUFA,
  CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS,
  CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS,
]

// NUTRIENT WHICH DV VALUE IS CACULATED BY CONSIDERING PRE ROUNDED VALUE of SERVING SIZE
export const CANADA_NUTRIENTS_DV_FROM_ORIGINAL_SERVE = [
  CANADA_NUTRITION_KEYS.TOTAL_FAT,
  CANADA_NUTRITION_KEYS.SATURATED_FAT,
  CANADA_NUTRITION_KEYS.TRANS_FAT,
  CANADA_NUTRITION_KEYS.DIETARY_FIBRE,
  CANADA_NUTRITION_KEYS.TOTAL_SUGARS,
]

export const CANADA_NO_DV_NUTRIENT_KEYS = [
  CANADA_NUTRITION_KEYS.ENERGY,
  CANADA_NUTRITION_KEYS.PUFA,
  CANADA_NUTRITION_KEYS.MUFA,
  CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS,
  CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS,
  CANADA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
  CANADA_NUTRITION_KEYS.PROTEIN,
]

export const CANADA_NON_MANDATORY_NUTRIENT_SEQUENCE = [
  CANADA_NUTRITION_KEYS.VITAMIN_A,
  CANADA_NUTRITION_KEYS.VITAMIN_C,
  CANADA_NUTRITION_KEYS.VITAMIN_D,
  CANADA_NUTRITION_KEYS.VITAMIN_E,
  CANADA_NUTRITION_KEYS.VITAMIN_K1,
  CANADA_NUTRITION_KEYS.VITAMIN_B1, // Thiamine
  CANADA_NUTRITION_KEYS.VITAMIN_B2, // Riboflavin
  CANADA_NUTRITION_KEYS.VITAMIN_B3, // Niacin
  CANADA_NUTRITION_KEYS.VITAMIN_B6,
  CANADA_NUTRITION_KEYS.FOLIC_ACID,
  CANADA_NUTRITION_KEYS.VITAMIN_B12,
  CANADA_NUTRITION_KEYS.BIOTIN,
  CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID,
  CANADA_NUTRITION_KEYS.CHOLINE,
  CANADA_NUTRITION_KEYS.PHOSPHORUS,
  CANADA_NUTRITION_KEYS.IODINE,
  CANADA_NUTRITION_KEYS.MAGNESIUM,
  CANADA_NUTRITION_KEYS.ZINC,
  CANADA_NUTRITION_KEYS.SELENIUM,
  CANADA_NUTRITION_KEYS.COPPER,
  CANADA_NUTRITION_KEYS.MANGANESE,
  CANADA_NUTRITION_KEYS.CHROMIUM,
  CANADA_NUTRITION_KEYS.MOLYBDENUM,
  CANADA_NUTRITION_KEYS.CHLORIDE,
]

export type CanadaNutrientData = {
  key: string
  unit: string
  name: string
  per_serve: string | number
  daily_value: string | number
}

export type CanadaMandatoryNutrientData = {
  FAT: CanadaNutrientData
  SATURATED: CanadaNutrientData
  TRANS: CanadaNutrientData
  CARBOHYDRATE: CanadaNutrientData
  FIBRE: CanadaNutrientData
  SUGARS: CanadaNutrientData
  PROTEIN: CanadaNutrientData
  CHOLESTEROL: CanadaNutrientData
  SODIUM: CanadaNutrientData
  POTASSIUM: CanadaNutrientData
  CALCIUM: CanadaNutrientData
  IRON: CanadaNutrientData
}

export type CanadaInterLeavedNonMandatoryNutrientData = Partial<{
  POLYUNSATURATED: CanadaNutrientData
  OMEGA_6: CanadaNutrientData
  OMEGA_3: CanadaNutrientData
  MONOUNSATURATED: CanadaNutrientData
}>

export type CanadaNutritionTableValues = {
  calories: number | string
  household_quantity_in_english: string
  household_quantity_in_french: string
  serving_size: number | string
  serving_size_unit: string
  mandatory_nutrient: CanadaMandatoryNutrientData
  interleaved_nutrient: CanadaInterLeavedNonMandatoryNutrientData
  non_mandatory_nutrient: CanadaNutrientData[]
}

export const CANADA_DV_VALUES = {
  "1-3 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 50.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 44.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 10.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 1200.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 14.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 7.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 700.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate ",
      daily_value: 150.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 90.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 0.5,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 0.9,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 3.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 80.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 15.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 0.5,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 0.5,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 8.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 6.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 2.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 300.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 2000.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 1500.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 400,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 11,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 17,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 460.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 10,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "4-6 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "7-9 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "10-12 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "13-15 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "16-18 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
  "Above 18 years": {
    [CANADA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Sugars / Sucres",
      daily_value: 100.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Fat / Lipides",
      daily_value: 75.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated / saturés",
      daily_value: 20.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol / Cholestérol",
      daily_value: 300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Fibre/Fibres",
      daily_value: 28.0,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron / Fer",
      daily_value: 18.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.FOLIC_ACID]: {
      nutrient_name: "Folate",
      daily_value: 400.0,
      unit: "µg DFE / ÉFA",
    },
    [CANADA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodide / Iode",
      daily_value: 150.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B₆ / Vitamine B₆",
      daily_value: 1.7,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B₁₂ / Vitamine B₁₂",
      daily_value: 2.4,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C / Vitamine C",
      daily_value: 90.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium / Magnésium ",
      daily_value: 420.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D / Vitamine D",
      daily_value: 20.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K / Vitamine K",
      daily_value: 120.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Thiamine",
      daily_value: 1.2,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Riboflavin / Riboflavine",
      daily_value: 1.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin / Biotine",
      daily_value: 30.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Niacin / Niacine",
      daily_value: 16.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenate / Pantothéinate",
      daily_value: 5.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E / Vitamine E",
      daily_value: 15.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A / Vitamine A",
      daily_value: 900.0,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium / Sélénium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper / Cuivre ",
      daily_value: 0.9,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3400.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHLORIDE]: {
      nutrient_name: "Chloride / Chlorure",
      daily_value: 2300.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Choline",
      daily_value: 550,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.CHROMIUM]: {
      nutrient_name: "Chromium / Chrome",
      daily_value: 35,
      unit: "µg",
    },
    [CANADA_NUTRITION_KEYS.MOLYBDENUM]: {
      nutrient_name: "Molybdenum / Molybdène",
      daily_value: 45,
      unit: "mcg",
    },
    [CANADA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese / Manganèse",
      daily_value: 2.3,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorous / Phosphore",
      daily_value: 1250.0,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy (kcal)",
      daily_value: null,
      unit: "kcal",
    },
    [CANADA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein / Protéines",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "+Trans / trans",
      daily_value: 20,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [CANADA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Monounsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega-3 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
    [CANADA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega-6 Polyunsaturated",
      daily_value: null,
      unit: "g",
    },
  },
}
